import { useEffect, useState } from "react";
import CourseCard from "./CourseCard";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import Loader from "../Live/Loader";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import CourseDetailsModal from "./CourseDetailsModal";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: { delay: custom * 0.2, duration: 0.5 },
  }),
};

const CoursesSection = () => {
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState(null);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  // Add state for modal and selected course
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingCourseDetails, setLoadingCourseDetails] = useState(false);

  const fetchCourses = async () => {
    setLoader(true);
    try {
      const { data } = await axios.get(API.Exposed.allCourses, {
        params: { limit: 4 },
      });

      if (data.status === 200) {
        setCourses(data.data.items);
      } else {
        setError(
          "Oops something went wrong, please refresh page and try again"
        );
      }
    } catch (error) {
      console.log(error);
      setError("Oops something went wrong, please refresh page and try again");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleSeeMore = () => {
    navigate("/courses");
  };

  // Add function to handle course selection
  const handleCourseSelect = async (courseId) => {
    setLoadingCourseDetails(true);
    try {
      const { data } = await axios.get(
        `${API.Exposed.courseDetails}/${courseId}`
      );
      if (data.status === 200) {
        setSelectedCourse(data.data);
        setIsModalOpen(true);
      } else {
        console.error("Failed to load course details");
      }
    } catch (error) {
      console.error("Error fetching course details:", error);
    } finally {
      setLoadingCourseDetails(false);
    }
  };

  // Add function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
    // Optional: Clear selected course after animation completes
    setTimeout(() => setSelectedCourse(null), 300);
  };

  return (
    <section
      className="bg-white py-8 sm:py-12 md:py-16 px-4 sm:px-8 md:px-12 lg:px-16 xl:px-28"
      id="courses"
    >
      <div className="container mx-auto">
        <motion.h2
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="font-anek-odia text-3xl sm:text-4xl md:text-5xl font-bold text-center text-blue-800 mb-6 sm:mb-8 md:mb-12"
        >
          Our <span className="font-normal">Courses</span>
        </motion.h2>

        {loader && <Loader />}
        {error ? (
          <div className="flex justify-center items-center">
            <span className="text-base sm:text-lg md:text-xl text-red-500">
              {error}
            </span>
          </div>
        ) : (
          <>
            <motion.div
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 lg:gap-8"
            >
              {courses.map((course, index) => (
                <motion.div
                  key={index}
                  custom={index + 2}
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <CourseCard
                    title={course.title}
                    description={course.description}
                    imageUrl={course.image_url}
                    price={course.price}
                    duration={course.duration}
                    level={course.level}
                    instructor={course.instructor_name}
                    category={course.category?.title}
                    courseId={course.id}
                    onViewCourse={() => handleCourseSelect(course.id)}
                  />
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              custom={6}
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="flex justify-center pt-6 sm:pt-8 md:pt-10 mt-4 sm:mt-6 md:mt-8"
            >
              <motion.button
                onClick={handleSeeMore}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-white font-medium py-2 px-4 sm:py-3 sm:px-6 rounded-md hover:bg-primary-hover transition duration-300"
              >
                See More ....
              </motion.button>
            </motion.div>
          </>
        )}
      </div>

      {/* Course Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedCourse && (
          <CourseDetailsModal
            course={selectedCourse}
            isOpen={isModalOpen}
            onClose={closeModal}
            loading={loadingCourseDetails}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default CoursesSection;
